import type { SettingsGroupId } from "@helm/ui";

/**
 * "Open Settings at this section" from outside the Settings dialog (the coach banner opens
 * Settings → Risk limits → Discipline coach; the palette's risk command opens Risk limits).
 * Settings already open switches section; Settings opened next starts on the requested one.
 *
 * Ids are a group (`SettingsGroupId`), a part of one ("account", "firm", "coach" …) or a
 * pre-regroup tab id ("risk", "platform", "discipline" …); `resolveSettingsTab` (@helm/ui) maps
 * each to where it lives now, so old links keep working.
 */
export type SettingsTabId =
  | SettingsGroupId
  // Pre-regroup tab ids (owner 2026-10-04 regroup): still accepted.
  | "rithmic"
  | "appearance"
  | "platform"
  | "brackets"
  | "discipline"
  | "data"
  | "devices"
  | "users"
  // Parts of a group.
  | "account"
  | "diagnostics"
  | "limits"
  | "firm"
  | "coach";

let pending: SettingsTabId | null = null;
const listeners = new Set<(tab: SettingsTabId) => void>();

export function requestSettingsTab(tab: SettingsTabId): void {
  pending = listeners.size === 0 ? tab : null;
  for (const l of listeners) l(tab);
}

/** The tab asked for before any Settings panel was open (consumed once). */
export function takeRequestedSettingsTab(): SettingsTabId | null {
  const t = pending;
  pending = null;
  return t;
}

export function onSettingsTabRequest(l: (tab: SettingsTabId) => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
